import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import './SendDialog.css'; // Reuse SendDialog.css for consistent styling

const ResendDialog = ({ project, contacts, emails, customerContactIds, surveys, surveyIds, uniqueIds, onClose, onSave }) => {
  const [selectedContacts, setSelectedContacts] = useState(contacts); // Prefill with all contacts
  const [selectAll, setSelectAll] = useState(true); // Select all by default
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedSurveyId, setSelectedSurveyId] = useState(''); // Initialize empty
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const dropdownRef = useRef(null);

  // const surveyBaseUrl = process.env.REACT_APP_SURVEY_BASE_URL;
  const surveyBaseUrl = window._env_.REACT_APP_SURVEY_BASE_URL;

  const safeContacts = Array.isArray(contacts) ? contacts : [];
  const safeEmails = Array.isArray(emails) ? emails : [];
  const safeCustomerContactIds = Array.isArray(customerContactIds) ? customerContactIds : [];
  const safeSurveyIds = Array.isArray(surveyIds) ? surveyIds : [];
  const safeUniqueIds = Array.isArray(uniqueIds) ? uniqueIds : [];
  const safeSurveys = Array.isArray(surveys) ? surveys : [];

  // Map contacts to their emails, contact IDs, and unique IDs
  const contactEmailMap = safeContacts.reduce((map, contact, index) => {
    const email = safeEmails[index] || '';
    const contactId = safeCustomerContactIds[index] || '';
    const uniqueId = safeUniqueIds[index] || '';
    map[contact] = { email, contactId, uniqueId };
    return map;
  }, {});

  // Initialize selectedSurveyId with the first valid survey ID
  useEffect(() => {
    if (safeSurveyIds.length > 0 && safeSurveys.length > 0) {
      const firstValidSurveyId = safeSurveyIds.find(id =>
        safeSurveys.some(survey => survey.SurveyID === parseInt(id))
      );
      if (firstValidSurveyId) {
        setSelectedSurveyId(firstValidSurveyId);
      } else if (safeSurveys[0]?.SurveyID) {
        setSelectedSurveyId(safeSurveys[0].SurveyID.toString());
      }
    }
  }, [safeSurveyIds, safeSurveys]);
  
  useEffect(() => {
    if (!selectedSurveyId || safeSurveys.length === 0) {
      setSubject('');
      setBody('No survey selected.');
      return;
    }

    const selectedSurvey = safeSurveys.find(survey => survey.SurveyID === parseInt(selectedSurveyId));
    if (selectedSurvey) {
      const projectName = project?.['Project Name'] || 'Unknown Project';
      setSubject(`CriticalRiver Feedback Request: Help Us Improve Your Experience with ${selectedSurvey.Name} : ${projectName}`);

      // Generate body with existing survey links for selected contacts
      const surveyLinks = safeContacts
        .filter(contact => selectedContacts.includes(contact))
        .map(contact => {
          const uniqueId = contactEmailMap[contact]?.uniqueId || '';
          return uniqueId ? `${contact}: ${surveyBaseUrl}${uniqueId}` : `${contact}: No survey link available`;
        })
        .join('\n');
      setBody(surveyLinks || 'No contacts selected.');
    } else {
      setSubject('');
      setBody('Selected survey not found.');
    }
  }, [selectedSurveyId, safeSurveys, surveyBaseUrl, safeContacts, project, selectedContacts, contactEmailMap]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleContactToggle = (contact) => {
    setSelectedContacts(prev => {
      const newSelected = prev.includes(contact)
        ? prev.filter(c => c !== contact)
        : [...prev, contact];
      setSelectAll(newSelected.length === safeContacts.length);
      return newSelected;
    });
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedContacts([]);
      setSelectAll(false);
    } else {
      setSelectedContacts([...safeContacts]);
      setSelectAll(true);
    }
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(prev => !prev);
  };

  const handleSave = () => {
    if (selectedContacts.length === 0 || !selectedSurveyId) {
      alert('Please select at least one contact and a survey.');
      return;
    }

    const selectedSurvey = safeSurveys.find(survey => survey.SurveyID === parseInt(selectedSurveyId));
    if (!selectedSurvey) {
      alert('Selected survey is invalid.');
      return;
    }

    // Prepare data to pass back to Projects.js
    const resendData = selectedContacts.map(contact => ({
      contactName: contact,
      email: contactEmailMap[contact]?.email || '',
      contactId: contactEmailMap[contact]?.contactId || '',
      uniqueId: contactEmailMap[contact]?.uniqueId || '',
      surveyId: parseInt(selectedSurveyId),
      surveyType: selectedSurvey?.Type || 'N/A',
      projectName: project?.['Project Name'] || 'Unknown Project',
      projectId: project?.id || 1,
      subject: subject,
      body: `${surveyBaseUrl}${contactEmailMap[contact]?.uniqueId || ''}`,
      sentBy: parseInt(sessionStorage.getItem('user_id')) || 1,
    }));

    // Call onSave with the collected data
    onSave(resendData);
    onClose();
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h2>Resend Survey</h2>
          <button className="close-btn" onClick={onClose}>
            ×
          </button>
        </div>
        <div className="form-body">
          <div className="form-row">
            <div className="form-group">
              <label>Select Contact(s)</label>
              <div className="custom-dropdown" ref={dropdownRef}>
                <div className="custom-dropdown-toggle" onClick={toggleDropdown}>
                  <span>
                    {selectedContacts.length > 0
                      ? `${selectedContacts.length} contact(s) selected`
                      : 'Select Contact'}
                  </span>
                </div>
                {isDropdownOpen && (
                  <div className="custom-dropdown-menu">
                    <div
                      className={`custom-dropdown-item select-all ${selectAll ? 'selected' : ''}`}
                      onClick={handleSelectAll}
                    >
                      <input
                        type="checkbox"
                        checked={selectAll}
                        onChange={handleSelectAll}
                      />
                      Select All
                    </div>
                    {safeContacts.map((contact, index) => (
                      <div
                        key={`contact-${index}`}
                        className={`custom-dropdown-item ${selectedContacts.includes(contact) ? 'selected' : ''}`}
                        onClick={() => handleContactToggle(contact)}
                      >
                        <input
                          type="checkbox"
                          checked={selectedContacts.includes(contact)}
                          onChange={() => handleContactToggle(contact)}
                        />
                        {contact}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="form-group">
              <label>Survey</label>
              <select
                value={selectedSurveyId}
                onChange={(e) => setSelectedSurveyId(e.target.value)}
              >
                <option value="">Select Survey</option>
                {safeSurveys.map((survey, index) => (
                  <option key={`survey-${index}`} value={survey.SurveyID}>
                    {survey.Name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group full-width">
              <label>Subject</label>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
              />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group full-width">
              <label>Body</label>
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows="5"
              />
            </div>
          </div>
        </div>
        <div className="form-actions">
          <button className="cancel-btn" onClick={onClose}>
            Cancel
          </button>
          <button className="save-btn" onClick={handleSave}>
            Send
          </button>
        </div>
      </div>
    </div>
  );
};

ResendDialog.propTypes = {
  project: PropTypes.object,
  contacts: PropTypes.arrayOf(PropTypes.string).isRequired,
  emails: PropTypes.arrayOf(PropTypes.string).isRequired,
  customerContactIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  surveys: PropTypes.arrayOf(PropTypes.object).isRequired,
  surveyIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  uniqueIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

ResendDialog.defaultProps = {
  project: {},
  contacts: [],
  emails: [],
  customerContactIds: [],
  surveys: [],
  surveyIds: [],
  uniqueIds: [],
};

export default ResendDialog;